import { Component, OnInit, TemplateRef, ViewChild, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { AdminService } from '../services/admin/admin.service';
import { HelpersService } from '../../shared/servicio/helpers/helpers.service';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-badges',
  templateUrl: './badges.component.html',
  styleUrls: ['./badges.component.css']
})
export class BadgesComponent implements OnInit, OnDestroy {

  @ViewChild('createTemplate') createTemplate: TemplateRef<any>;
  @ViewChild('editTemplate') editTemplate: TemplateRef<any>;
  @ViewChild('deleteTemplate') deleteTemplate: TemplateRef<any>;

  // Modal
  modalRef: BsModalRef;

  // Form
  badgeForm: FormGroup;
  submitted = false;

  // Loading y errores
  showLoading = true;
  preloader = false;
  div_error = 0;
  div_text_error = '';

  // Filtros
  criterioSelectorFC = this.fb.control('name');
  searchTextFC = this.fb.control('');
  statusFilterFC = this.fb.control('');

  // Estados de filtros
  isActiveON = true;
  isInactiveON = true;

  // Datos
  list_badges: any[] = [];
  selectedBadge: any = null;

  // Paginación
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  // Filtros aplicados
  activeFilter: boolean | undefined = undefined;
  searchFilter: any;
  statusFilter: any;

  // Imagen
  selectedImage: File | null = null;
  imagePreview: string | null = null;
  imageError = '';

  // Opciones de iconos
  iconOptions = [
    { value: 'star', label: 'Estrella', icon: 'fas fa-star' },
    { value: 'trophy', label: 'Trofeo', icon: 'fas fa-trophy' },
    { value: 'medal', label: 'Medalla', icon: 'fas fa-medal' },
    { value: 'crown', label: 'Corona', icon: 'fas fa-crown' },
    { value: 'gem', label: 'Gema', icon: 'far fa-gem' },
    { value: 'shield', label: 'Escudo', icon: 'fas fa-shield-alt' },
    { value: 'heart', label: 'Corazón', icon: 'fas fa-heart' },
    { value: 'fire', label: 'Fuego', icon: 'fas fa-fire' },
    { value: 'bolt', label: 'Rayo', icon: 'fas fa-bolt' },
    { value: 'thumbs-up', label: 'Pulgar Arriba', icon: 'fas fa-thumbs-up' },
    { value: 'certificate', label: 'Certificado', icon: 'fas fa-certificate' },
    { value: 'award', label: 'Premio', icon: 'fas fa-award' }
  ];

  // Opciones de colores
  colorOptions = [
    { value: '#FFD700', label: 'Dorado', color: '#FFD700' },
    { value: '#C0C0C0', label: 'Plateado', color: '#C0C0C0' },
    { value: '#CD7F32', label: 'Bronce', color: '#CD7F32' },
    { value: '#FF6B6B', label: 'Rojo', color: '#FF6B6B' },
    { value: '#4ECDC4', label: 'Turquesa', color: '#4ECDC4' },
    { value: '#45B7D1', label: 'Azul', color: '#45B7D1' },
    { value: '#96CEB4', label: 'Verde', color: '#96CEB4' },
    { value: '#FFEAA7', label: 'Amarillo', color: '#FFEAA7' },
    { value: '#DDA0DD', label: 'Púrpura', color: '#DDA0DD' },
    { value: '#FFA07A', label: 'Naranja', color: '#FFA07A' }
  ];

  // Destroy subject
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private modalService: BsModalService,
    private adminService: AdminService,
    public helper: HelpersService
  ) {
    this.initForm();
    this.setupSearchFilters();
  }

  ngOnInit() {
    // Asegurar que las propiedades estén inicializadas
    this.list_badges = [];
    this.selectedBadge = null;
    this.loadBadges();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Getter para acceso fácil a los controles del formulario
  get f() { return this.badgeForm.controls; }

  /**
   * Inicializar formulario
   */
  initForm() {
    this.badgeForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      description: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(500)]],
      icon: ['', [Validators.required]],
      color: ['', [Validators.required]],
      points_reward: ['', [Validators.required, Validators.min(1), Validators.max(999999)]],
      is_active: [true],
      // Nuevos campos de requisitos
      req_referrals: ['', [Validators.min(0), Validators.max(999999)]],
      req_effective_rate: ['', [Validators.min(0), Validators.max(100)]],
      req_points: ['', [Validators.min(0), Validators.max(999999)]],
      req_consecutive_days: ['', [Validators.min(0), Validators.max(999)]],
      req_monthly_referrals: ['', [Validators.min(0), Validators.max(999999)]],
      req_monthly_effective_referrals: ['', [Validators.min(0), Validators.max(999999)]]
    });
  }

  /**
   * Configurar filtros de búsqueda con debounce
   */
  setupSearchFilters() {
    this.searchTextFC.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        this.searchFilter = value || '';
        this.currentPage = 1;
        this.loadBadges();
      });

    this.statusFilterFC.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.statusFilter = value || '';
        this.currentPage = 1;
        this.loadBadges();
      });
  }

  /**
 * Cargar badges
 */
  loadBadges() {
    this.showLoading = true;

    const searchTerm = this.getSearchTerm();

    this.adminService.listBadges(
      this.currentPage,
      this.pageSize,
      this.activeFilter,
      searchTerm
    ).subscribe({
      next: (response) => {
        let badgesList = [];

        if (Array.isArray(response)) {
          badgesList = response;
          this.totalItems = response.length;
        } else {
          badgesList = response.data || response || [];
          this.totalItems = response.count || badgesList.length;
        }

        // Validar y limpiar datos de badges
        this.list_badges = badgesList.map(badge => {
          return {
            ...badge,
            requirements: badge.requirements && typeof badge.requirements === 'object'
              ? badge.requirements
              : {}
          };
        });

        this.totalPages = Math.ceil(this.totalItems / this.pageSize);
        this.showLoading = false;
      },
      error: (error) => {
        console.error('Error loading badges:', error);
        this.showLoading = false;
        this.list_badges = []; // Asegurar que sea un array vacío
        this.helper.modalError('Error al cargar los badges');
      }
    });
  }

  /**
   * Obtener término de búsqueda basado en el criterio seleccionado
   */
  getSearchTerm(): string {
    if (!this.searchFilter) return '';

    const criterio = this.criterioSelectorFC.value;

    switch (criterio) {
      case 'name':
        return this.searchFilter;
      case 'description':
        return this.searchFilter;
      default:
        return this.searchFilter;
    }
  }

  /**
   * Toggle filtro activos
   */
  onActiveToggle() {
    this.isActiveON = !this.isActiveON;
    this.updateActiveFilter();
  }

  /**
   * Toggle filtro inactivos
   */
  onInactiveToggle() {
    this.isInactiveON = !this.isInactiveON;
    this.updateActiveFilter();
  }

  /**
   * Actualizar filtro de activos/inactivos
   */
  updateActiveFilter() {
    if (this.isActiveON && this.isInactiveON) {
      this.activeFilter = undefined; // Mostrar todos
    } else if (this.isActiveON) {
      this.activeFilter = true; // Solo activos
    } else if (this.isInactiveON) {
      this.activeFilter = false; // Solo inactivos
    } else {
      this.activeFilter = undefined; // Mostrar todos si ninguno está seleccionado
    }

    this.currentPage = 1;
    this.loadBadges();
  }

  /**
   * Cambiar página
   */
  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadBadges();
    }
  }

  /**
   * Abrir modal crear
   */
  openCreateModal() {
    this.resetForm();
    this.selectedBadge = null;
    this.modalRef = this.modalService.show(this.createTemplate, {
      class: 'modal-xl',
      backdrop: 'static'
    });
  }

  /**
   * Abrir modal editar
   */
  openEditModal(badge: any) {
    this.resetForm();
    this.selectedBadge = badge;
    this.populateForm(badge);
    this.modalRef = this.modalService.show(this.editTemplate, {
      class: 'modal-xl',
      backdrop: 'static'
    });
  }

  /**
   * Abrir modal eliminar
   */
  openDeleteModal(badge: any) {
    this.selectedBadge = badge;
    this.modalRef = this.modalService.show(this.deleteTemplate, {
      backdrop: 'static'
    });
  }

  /**
   * Cerrar modal
   */
  closeModal() {
    this.modalRef.hide();
    this.resetForm();
  }

  /**
   * Reset formulario
   */
  resetForm() {
    this.badgeForm.reset({
      is_active: true
    });
    this.submitted = false;
    this.div_error = 0;
    this.div_text_error = '';
    this.selectedImage = null;
    this.imagePreview = null;
    this.imageError = '';
  }

  /**
   * Poblar formulario con datos del badge
   */
  populateForm(badge: any) {
    this.badgeForm.patchValue({
      name: badge.name,
      description: badge.description,
      icon: badge.icon,
      color: badge.color,
      points_reward: badge.points_reward, // Cambiar de points_required a points_reward
      is_active: badge.is_active,
      // Requisitos desde el objeto requirements
      req_referrals: badge.requirements.referrals || '',
      req_effective_rate: badge.requirements.effective_rate || '',
      req_points: badge.requirements.points || '',
      req_consecutive_days: badge.requirements.consecutive_days || '',
      req_monthly_referrals: badge.requirements.monthly_referrals || '',
      req_monthly_effective_referrals: badge.requirements.monthly_effective_referrals || ''
    });

    if (badge.image) {
      this.imagePreview = badge.image;
    }
  }

  /**
   * Submit formulario
   */
  onSubmit() {
    this.submitted = true;
    this.div_error = 0;

    if (this.badgeForm.invalid) {
      return;
    }

    this.preloader = true;

    // Si hay una imagen seleccionada, la convertimos a base64
    if (this.selectedImage) {
      this.convertImageToBase64(this.selectedImage).then(base64Image => {
        this.sendBadgeData(base64Image);
      }).catch(error => {
        this.preloader = false;
        this.div_error = 1;
        this.div_text_error = 'Error al procesar la imagen';
        console.error('Error converting image:', error);
      });
    } else {
      // Si no hay imagen nueva, enviamos la preview (que ya está en base64) o null
      this.sendBadgeData(this.imagePreview || null);
    }
  }

  hasValidRequirements(badge: any): boolean {
    return badge &&
      badge.requirements &&
      typeof badge.requirements === 'object' &&
      badge.requirements !== null &&
      Object.keys(badge.requirements).length > 0;
  }

  /**
   * Obtener las keys de requisitos de forma segura
   */
  getRequirementsKeys(requirements: any): string[] {
    if (!requirements || typeof requirements !== 'object') {
      return [];
    }
    try {
      return Object.keys(requirements);
    } catch (error) {
      console.error('Error getting requirements keys:', error);
      return [];
    }
  }

  /**
   * Convertir imagen a base64
   */
  private convertImageToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  }

  /**
   * Enviar datos del badge al servidor
   */
  private sendBadgeData(imageBase64: string | null) {
    const formValues = this.badgeForm.value;

    // Construir objeto requirements solo con valores que tienen contenido
    const requirements: any = {};

    if (formValues.req_referrals !== '' && formValues.req_referrals !== null) {
      requirements.referrals = parseInt(formValues.req_referrals);
    }
    if (formValues.req_effective_rate !== '' && formValues.req_effective_rate !== null) {
      requirements.effective_rate = parseFloat(formValues.req_effective_rate);
    }
    if (formValues.req_points !== '' && formValues.req_points !== null) {
      requirements.points = parseInt(formValues.req_points);
    }
    if (formValues.req_consecutive_days !== '' && formValues.req_consecutive_days !== null) {
      requirements.consecutive_days = parseInt(formValues.req_consecutive_days);
    }
    if (formValues.req_monthly_referrals !== '' && formValues.req_monthly_referrals !== null) {
      requirements.monthly_referrals = parseInt(formValues.req_monthly_referrals);
    }
    if (formValues.req_monthly_effective_referrals !== '' && formValues.req_monthly_effective_referrals !== null) {
      requirements.monthly_effective_referrals = parseInt(formValues.req_monthly_effective_referrals);
    }

    const badgeData = {
      name: formValues.name,
      description: formValues.description,
      icon: formValues.icon,
      color: formValues.color,
      points_reward: parseInt(formValues.points_reward),
      is_active: formValues.is_active,
      requirements: requirements,
      image: imageBase64 || null
    };

    const operation$ = this.selectedBadge
      ? this.adminService.updateBadge(this.selectedBadge.id, badgeData)
      : this.adminService.createBadge(badgeData);

    operation$.subscribe({
      next: (response) => {
        this.preloader = false;
        this.helper.modalSuccess(`¡Badge ${this.selectedBadge ? 'actualizado' : 'creado'} exitosamente!`);
        this.closeModal();
        this.loadBadges();
      },
      error: (error) => {
        this.preloader = false;
        console.error('Error saving badge:', error);
        this.div_error = 1;
        this.div_text_error = error.error.message || 'Error al guardar el badge';
        this.helper.modalError(`Error al ${this.selectedBadge ? 'actualizar' : 'crear'} el badge`);
      }
    });
  }

  /**
   * Eliminar badge
   */
  deleteBadge() {
    if (!this.selectedBadge) return;

    this.preloader = true;

    this.adminService.deleteBadge(this.selectedBadge.id).subscribe({
      next: () => {
        this.preloader = false;
        this.helper.modalSuccess('¡Badge eliminado exitosamente!');
        this.closeModal();
        this.loadBadges();
      },
      error: (error) => {
        this.preloader = false;
        console.error('Error deleting badge:', error);
        this.helper.modalError('Error al eliminar el badge');
      }
    });
  }

  /**
   * Toggle estado del badge
   */
  toggleBadgeStatus(badge: any) {
    const newStatus = !badge.is_active;

    this.helper.modalConfirm(
      `¿Está seguro que desea ${newStatus ? 'activar' : 'desactivar'} este badge?`
    ).then((result) => {
      if (result.value) {
        this.adminService.toggleBadgeStatus(badge.id, newStatus).subscribe({
          next: () => {
            badge.is_active = newStatus;
            this.helper.modalSuccess(`¡Badge ${newStatus ? 'activado' : 'desactivado'} exitosamente!`);
          },
          error: (error) => {
            console.error('Error updating status:', error);
            this.helper.modalError('Error al actualizar el estado del badge');
          }
        });
      }
    });
  }

  /**
   * Manejo de selección de imagen
   */
  onImageSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      this.imageError = 'Solo se permiten archivos de imagen';
      return;
    }

    // Validar tamaño (máximo 2MB)
    if (file.size > 2 * 1024 * 1024) {
      this.imageError = 'La imagen debe ser menor a 2MB';
      return;
    }

    this.selectedImage = file;
    this.imageError = '';

    // Crear preview (ya en base64)
    const reader = new FileReader();
    reader.onload = (e) => {
      this.imagePreview = (e.target as FileReader).result as string;
    };
    reader.readAsDataURL(file);
  }

  /**
   * Remover imagen seleccionada
   */
  removeImage() {
    this.selectedImage = null;
    this.imagePreview = null;
    this.imageError = '';
  }

  /**
   * Obtener icono para mostrar
   */
  getIconClass(iconValue: string): string {
    const icon = this.iconOptions.find(opt => opt.value === iconValue);
    return icon ? icon.icon : 'fas fa-star';
  }

  /**
   * Obtener label del icono
   */
  getIconLabel(iconValue: string): string {
    const icon = this.iconOptions.find(opt => opt.value === iconValue);
    return icon ? icon.label : iconValue;
  }

  /**
   * Obtener clase CSS para el estado
   */
  getStatusClass(badge: any): string {
    return badge.is_active ? 'text-success' : 'text-danger';
  }

  /**
   * Formatear fecha
   */
  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  }

  /**
   * Formatear puntos como número
   */
  formatPoints(points: number): string {
    return new Intl.NumberFormat('es-ES').format(points);
  }

  /**
   * Generar texto descriptivo de requisitos
   */
  /**
  * Generar texto descriptivo de requisitos
  */
  getRequirementsText(badge: any): string {
    if (!this.hasValidRequirements(badge)) {
      return 'Sin requisitos específicos';
    }

    const requirements = [];
    const reqs = badge.requirements;

    try {
      if (reqs.referrals && typeof reqs.referrals === 'number') {
        requirements.push(`${this.formatPoints(reqs.referrals)} referidos totales`);
      }

      if (reqs.effective_rate && typeof reqs.effective_rate === 'number') {
        requirements.push(`${reqs.effective_rate}% de efectividad`);
      }

      if (reqs.points && typeof reqs.points === 'number') {
        requirements.push(`${this.formatPoints(reqs.points)} puntos acumulados`);
      }

      if (reqs.consecutive_days && typeof reqs.consecutive_days === 'number') {
        requirements.push(`${reqs.consecutive_days} días consecutivos`);
      }

      if (reqs.monthly_referrals && typeof reqs.monthly_referrals === 'number') {
        requirements.push(`${this.formatPoints(reqs.monthly_referrals)} referidos mensuales`);
      }

      if (reqs.monthly_effective_referrals && typeof reqs.monthly_effective_referrals === 'number') {
        requirements.push(`${this.formatPoints(reqs.monthly_effective_referrals)} referidos efectivos mensuales`);
      }

      return requirements.length > 0 ? requirements.join(' • ') : 'Sin requisitos específicos';
    } catch (error) {
      console.error('Error generating requirements text:', error);
      return 'Error al cargar requisitos';
    }
  }
}
